import React, { useState } from 'react';
import { useGetSubscriptionCardsQuery, useMakeSubscriptionPaymentMutation, useDeleteSubscriptionCardMutation, usePurchasePackageMutation } from '../../store/api/landingApi';
import { useSearchParams, useNavigate } from 'react-router-dom';
import PageLoader from '../../components/PageLoader';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { toast } from 'react-toastify';

const CheckoutForm = ({ plan, cards, userId, language, refetchCards }: any) => {
  const stripe = useStripe();
  const elements = useElements();
  const [makePayment, { isLoading: isPaying }] = useMakeSubscriptionPaymentMutation();
  const [deleteCard] = useDeleteSubscriptionCardMutation();
  const [purchasePackage, { isLoading: isPurchasing }] = usePurchasePackageMutation();
  const [selectedCardId, setSelectedCardId] = useState<string | null>(cards?.data && cards.data.length > 0 ? cards.data[0].id : null);
  const [showNewCardForm, setShowNewCardForm] = useState(cards?.data && cards.data.length > 0 ? false : true);
  const [saveCard, setSaveCard] = useState(false);
  const [cardName, setCardName] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (showNewCardForm) {
      if (!stripe || !elements) return;

      const cardElement = elements.getElement(CardElement);
      if (!cardElement) return;

      const tokenResult = await stripe.createToken(cardElement, { name: cardName });
      
      if (tokenResult.error) {
          toast.error(tokenResult.error.message);
          return;
      }

      handlePaymentApi(tokenResult.token.id, null);

    } else {
      if (!selectedCardId) {
        toast.error("Please select a card");
        return;
      }
      handlePaymentApi(null, selectedCardId);
    }
  };

  const handlePaymentApi = async (stripeToken: string | null, cardId: string | null) => {
    try {
      const payload: any = {
        user_id: userId,
        plan: plan.id,
        amount: plan.price,
        language: language,
        save_card: saveCard
      };

      if (stripeToken) {
        payload.stripeToken = stripeToken;
      } else {
        payload.cardId = cardId;
      }

      const response = await makePayment(payload).unwrap();
      if (response.status) {
        
        // After payment charged, call purchasePackage API
        try {
            const purchasePayload = {
                user_id: userId,
                package_id: plan.id,
                language: language
            };
            const purchaseResponse = await purchasePackage(purchasePayload).unwrap();
            
            if (purchaseResponse.status) {
                toast.success(response.message);
                // Redirect to my-subscription page
                navigate(`/my-subscription?user_id=${userId}&language=${language}`);
            } else {
                toast.error(purchaseResponse.message || "Purchase failed after payment");
            }
        } catch (purchaseErr: any) {
             console.error("Purchase API Error", purchaseErr);
             // Even if this fails, payment succeeded, so maybe still redirect or show specific error?
             // But the user requirement is explicit: call API -> redirect.
             toast.error(purchaseErr.data?.message || "Failed to record purchase");
        }

      } else {
        toast.error(response.message);
      }
    } catch (err: any) {
      toast.error(err.data?.message || "Payment failed");
    }
  };

  const handleDeleteCard = async (cardId: string) => {
      if(!window.confirm("Are you sure you want to remove this card?")) return;
      
      try {
          const res = await deleteCard({ user_id: userId, delCard: cardId }).unwrap();
          if(res.status) {
              toast.success(res.message);
              refetchCards();
          } else {
              toast.error(res.message);
          }
      } catch (err: any) {
          toast.error(err.data?.message || "Failed to delete card");
      }
  }

  const addCardText = language === 'ar' ? "ادفع باستخدام بطاقة جديدة" : "Pay With New Card";
  const cardNameLabel = language === 'ar' ? "الاسم على البطاقة" : "Name on Card";
  const cardNoLabel = language === 'ar' ? "بطاقة الائتمان أو الخصم" : "Credit or Debit Card";
  const saveCardText = language === 'ar' ? "هل يمكن الحفظ التلقائي لمعلومات الدفع بالبطاقة ليتم تجديد الاشتراك كل شهر؟" : "can we save automatically without user's consent?";
  const myCardsText = language === 'ar' ? "بطاقاتي" : "My Cards";
  const checkoutText = language === 'ar' ? "الدفع" : "Checkout";
  const expiryText = language === 'ar' ? "تاريخ الانتهاء" : "Expiry";

  return (
    <div className="container mt-5">
      <div className="row">
        <div className="col-md-8 offset-md-2">
            
            {showNewCardForm && (
                <div className="card shadow-sm p-4 mb-4">
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <h4>{addCardText}</h4>
                        <button type="button" onClick={() => setShowNewCardForm(false)} className="btn btn-close"></button>
                    </div>
                    
                    <form onSubmit={handleSubmit}>
                        <div className="mb-3">
                            <label className="form-label">{cardNameLabel}</label>
                            <input 
                                type="text" 
                                className="form-control" 
                                required 
                                value={cardName}
                                onChange={(e) => setCardName(e.target.value)}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{cardNoLabel}</label>
                            <div className="form-control p-3">
                                <CardElement options={{
                                    hidePostalCode: true,
                                    style: {
                                        base: {
                                            fontSize: '16px',
                                            color: '#424770',
                                            '::placeholder': {
                                                color: '#aab7c4',
                                            },
                                        },
                                        invalid: {
                                            color: '#9e2146',
                                        },
                                    },
                                }} />
                            </div>
                        </div>
                        <div className="mb-3 form-check">
                            <input 
                                type="checkbox" 
                                className="form-check-input" 
                                id="saveCard" 
                                checked={saveCard}
                                onChange={(e) => setSaveCard(e.target.checked)}
                            />
                            <label className="form-check-label" htmlFor="saveCard">{saveCardText}</label>
                        </div>
                        <button type="submit" disabled={!stripe || isPaying || isPurchasing} className="btn btn-primary w-100">
                            {(isPaying || isPurchasing) ? 'Processing...' : checkoutText}
                        </button>
                    </form>
                </div>
            )}

            <div className="d-flex justify-content-between align-items-center mb-4">
                <h2 className={language === 'ar' ? 'rtl' : ''}>{myCardsText}</h2>
                <button className="btn btn-base btn-sm" onClick={() => setShowNewCardForm(true)}>{addCardText}</button>
            </div>

            <hr />

            {cards.data && cards.data.length > 0 ? (
                <div className="row">
                    {cards.data.map((card: any, index: number) => (
                        <div key={card.id} className="col-md-6 mb-3">
                            <div 
                                className={`card border shadow p-5 cursor-pointer ${selectedCardId === card.id ? 'border-success border-2' : ''}`}
                                onClick={() => { setSelectedCardId(card.id); setShowNewCardForm(false); }}
                                style={{ cursor: 'pointer', position: 'relative' }}
                            >
                                {selectedCardId === card.id && (
                                    <div style={{ position: 'absolute', top: '10px', right: '10px', color: '#36BD71', fontSize: '20px' }}>✓</div>
                                )}
                                <div className="fs-4 mb-2">**** **** **** {card.card.last4}</div>
                                <div className="d-flex justify-content-between text-muted small">
                                    <div>{expiryText}: {card.card.exp_month}/{card.card.exp_year}</div>
                                    <button 
                                        className="btn btn-sm btn-link text-danger p-0"
                                        onClick={(e) => { e.stopPropagation(); handleDeleteCard(card.id); }}
                                    >
                                        <i className="fas fa-trash-alt"></i>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <p>No saved cards found.</p>
            )}

            {!showNewCardForm && cards.data && cards.data.length > 0 && (
                 <div className="mt-4 p-4 border rounded shadow-sm bg-white">
                    <div className="row align-items-center">
                        <div className="col-6">
                            <h3 className="text-base">{language === 'ar' ? plan.name_ar : plan.name}</h3>
                            <h4>$ {plan.price}</h4>
                        </div>
                        <div className="col-6 text-end">
                            <button onClick={handleSubmit} disabled={isPaying} className="btn btn-base">
                                {isPaying ? 'Processing...' : checkoutText}
                            </button>
                        </div>
                    </div>
                 </div>
            )}

        </div>
      </div>
    </div>
  );
};

const SubscriptionPayment = () => {
  const [searchParams] = useSearchParams();
  const userId = searchParams.get('user_id');
  const planId = searchParams.get('plan');
  const language = searchParams.get('language') || 'en';

  const { data, isLoading, isError, refetch } = useGetSubscriptionCardsQuery({ 
    user_id: userId,
    plan: planId,
    language: language
  });

  if (isLoading) return <PageLoader />;
  if (isError) return <div className="text-center p-5">Error loading payment details</div>;

  const { cards, plan, stripe_key } = data?.data || {};

  if (!stripe_key) return <div>Missing Stripe Key</div>;

  const stripePromise = loadStripe(stripe_key);

  return (
    <>
       <div className="d-flex flex-column bg-base-gradient flex-center w-100 min-h-100px z-index-n1 min-h-lg-150px px-9 py-10">
         <div className="row w-100 mx-auto">
            <div className="col-md-8 mx-auto text-center">
                <h1 className="text-base-dark lh-base fw-bold fs-1x fs-lg-3x text-uppercase">
                    {language === 'ar' ? "طرق الدفع" : "Payment Methods"}
                </h1>
            </div>
         </div>
       </div>

       <div className="mb-15">
           <Elements stripe={stripePromise} options={{ locale: language === 'ar' ? 'ar' : 'en' }}>
              <CheckoutForm 
                  plan={plan} 
                  cards={cards} 
                  userId={userId} 
                  language={language} 
                  refetchCards={refetch}
              />
           </Elements>
       </div>
    </>
  );
};

export default SubscriptionPayment;
